/**
 * Client-side rate limiting for authentication attempts
 * This provides immediate feedback and reduces unnecessary server requests
 * Server-side rate limiting is also enforced by Supabase
 */

interface RateLimitEntry {
  attempts: number;
  firstAttempt: number;
  lastAttempt: number;
  lockedUntil: number | null;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  lockoutDurationMs: number;
  backoffMultiplier: number;
}

const AUTH_RATE_LIMIT_CONFIG: RateLimitConfig = {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000, // 15 minutes
  lockoutDurationMs: 5 * 60 * 1000, // 5 minutes initial lockout
  backoffMultiplier: 2, // Exponential backoff multiplier
};

const STORAGE_KEY = 'auth_rate_limit';

/**
 * Get rate limit data from localStorage
 */
function getRateLimitData(): Record<string, RateLimitEntry> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored);
  } catch {
    return {};
  }
}

/**
 * Save rate limit data to localStorage
 */
function saveRateLimitData(data: Record<string, RateLimitEntry>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Generate a key for rate limiting (combines identifier with action type)
 */
function getRateLimitKey(identifier: string, action: 'login' | 'signup'): string {
  // Hash the identifier to avoid storing email in localStorage
  let hash = 0;
  const str = identifier.toLowerCase();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `${action}_${Math.abs(hash).toString(36)}`;
}

/**
 * Check if an action is rate limited
 * Returns null if allowed, or an error message with lockout time if blocked
 */
export function checkRateLimit(
  identifier: string,
  action: 'login' | 'signup'
): { allowed: boolean; message?: string; retryAfterSeconds?: number } {
  const now = Date.now();
  const key = getRateLimitKey(identifier, action);
  const data = getRateLimitData();
  const entry = data[key];

  // Clean up old entries
  Object.keys(data).forEach((k) => {
    const e = data[k];
    if (e.lockedUntil && e.lockedUntil < now) {
      // Lockout expired, check if window also expired
      if (now - e.firstAttempt > AUTH_RATE_LIMIT_CONFIG.windowMs) {
        delete data[k];
      } else {
        // Reset lockout but keep attempt count
        e.lockedUntil = null;
      }
    } else if (!e.lockedUntil && now - e.firstAttempt > AUTH_RATE_LIMIT_CONFIG.windowMs) {
      delete data[k];
    }
  });
  saveRateLimitData(data);

  if (!entry) {
    return { allowed: true };
  }

  // Check if currently locked out
  if (entry.lockedUntil && entry.lockedUntil > now) {
    const retryAfterSeconds = Math.ceil((entry.lockedUntil - now) / 1000);
    const minutes = Math.ceil(retryAfterSeconds / 60);
    return {
      allowed: false,
      message: `Too many attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`,
      retryAfterSeconds,
    };
  }

  // Check if within rate limit window
  if (now - entry.firstAttempt < AUTH_RATE_LIMIT_CONFIG.windowMs) {
    if (entry.attempts >= AUTH_RATE_LIMIT_CONFIG.maxAttempts) {
      // Calculate exponential backoff lockout duration
      const lockoutCount = Math.floor(entry.attempts / AUTH_RATE_LIMIT_CONFIG.maxAttempts);
      const lockoutDuration = AUTH_RATE_LIMIT_CONFIG.lockoutDurationMs * 
        Math.pow(AUTH_RATE_LIMIT_CONFIG.backoffMultiplier, lockoutCount - 1);
      const maxLockout = 60 * 60 * 1000; // Cap at 1 hour
      const actualLockout = Math.min(lockoutDuration, maxLockout);
      
      entry.lockedUntil = now + actualLockout;
      saveRateLimitData(data);
      
      const retryAfterSeconds = Math.ceil(actualLockout / 1000);
      const minutes = Math.ceil(retryAfterSeconds / 60);
      return {
        allowed: false,
        message: `Too many attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`,
        retryAfterSeconds,
      };
    }
  }

  return { allowed: true };
}

/**
 * Record an authentication attempt
 */
export function recordAuthAttempt(
  identifier: string,
  action: 'login' | 'signup',
  success: boolean
): void {
  const now = Date.now();
  const key = getRateLimitKey(identifier, action);
  const data = getRateLimitData();

  if (success) {
    // Clear rate limit on successful auth
    delete data[key];
    saveRateLimitData(data);
    return;
  }

  // Record failed attempt
  const entry = data[key];
  if (!entry || now - entry.firstAttempt > AUTH_RATE_LIMIT_CONFIG.windowMs) {
    // Start new window
    data[key] = {
      attempts: 1,
      firstAttempt: now,
      lastAttempt: now,
      lockedUntil: null,
    };
  } else {
    // Increment attempts
    entry.attempts += 1;
    entry.lastAttempt = now;
  }

  saveRateLimitData(data);
}

/**
 * Clear rate limit for an identifier (e.g., after password reset)
 */
export function clearRateLimit(identifier: string, action: 'login' | 'signup'): void {
  const key = getRateLimitKey(identifier, action);
  const data = getRateLimitData();
  delete data[key];
  saveRateLimitData(data);
}

/**
 * Get remaining attempts before lockout
 */
export function getRemainingAttempts(
  identifier: string,
  action: 'login' | 'signup'
): number {
  const key = getRateLimitKey(identifier, action);
  const data = getRateLimitData();
  const entry = data[key];

  if (!entry) {
    return AUTH_RATE_LIMIT_CONFIG.maxAttempts;
  }

  const now = Date.now();
  if (now - entry.firstAttempt > AUTH_RATE_LIMIT_CONFIG.windowMs) {
    return AUTH_RATE_LIMIT_CONFIG.maxAttempts;
  }

  return Math.max(0, AUTH_RATE_LIMIT_CONFIG.maxAttempts - entry.attempts);
}
